/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { PrimaryButton, SecondaryButton } from "../../components";
import { useAlert } from "../../store/useAlert";
import { useAuth } from "../../store/useAuth";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";

interface UserResult {
  _id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface LocationResult {
  _id: string;
  name: string;
  city?: string;
  address?: string;
}

type Tab = "tomra" | "beevision";

const DEFAULT_TOMRA_ITEMS = [
  {
    count: 1,
    type: "single",
    barcode: "1234567890",
    productCode: "1234567890",
    refund: 150,
    superGroup: 10,
  },
];

const DEFAULT_AI_RESULT = "Plastic Packaging: 1, Milk Jerrycans: 1";

function WebhookSimulatorScreen() {
  const { user: currentUser } = useAuth();
  const { setAlert } = useAlert();

  const [tab, setTab] = useState<Tab>("tomra");

  // User search
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<UserResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserResult | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Location search
  const [allLocations, setAllLocations] = useState<LocationResult[]>([]);
  const [locationSearch, setLocationSearch] = useState("");
  const [selectedLocation, setSelectedLocation] =
    useState<LocationResult | null>(null);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const locationRef = useRef<HTMLDivElement>(null);

  // TOMRA
  const [tomraRefund, setTomraRefund] = useState("150");
  const [tomraItemsJson, setTomraItemsJson] = useState(
    JSON.stringify(DEFAULT_TOMRA_ITEMS, null, 2)
  );
  const [tomraLoading, setTomraLoading] = useState(false);
  const [tomraResult, setTomraResult] = useState<any>(null);

  // Beevision
  const [bvWeight, setBvWeight] = useState("0.5");
  const [bvAiResult, setBvAiResult] = useState(DEFAULT_AI_RESULT);
  const [bvStatusCode, setBvStatusCode] = useState("1");
  const [bvSerialNumber, setBvSerialNumber] = useState("SIM-001");
  const [bvLoadingState, setBvLoadingState] = useState<
    "idle" | "loading_sent" | "drop_sent"
  >("idle");
  const [bvActionLoading, setBvActionLoading] = useState(false);
  const [bvResult, setBvResult] = useState<any>(null);

  const isSuperAdmin = Number(currentUser?.user_role) === 7;

  // Load all locations once. On a hard refresh the request can fire before the
  // auth token is attached (→ 401) or come back empty, which left the location
  // picker blank. Retry a few times on failure/empty so it recovers.
  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const res = await axios.get("locations");
        const list: LocationResult[] = Array.isArray(res?.data) ? res.data : [];
        if (cancelled) return;
        if (list.length === 0 && attempt < 4) {
          setTimeout(() => load(attempt + 1), 600);
          return;
        }
        setAllLocations(list);
      } catch {
        if (!cancelled && attempt < 4) {
          setTimeout(() => load(attempt + 1), 600);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close location dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        locationRef.current &&
        !locationRef.current.contains(e.target as Node)
      ) {
        setLocationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setTomraResult(null);
    setBvResult(null);
    setBvLoadingState("idle");
  }, [tab, selectedUser?._id]);

  // User search with debounce
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (search.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(
          `users/all?page=1&limit=10&search=${encodeURIComponent(search.trim())}`
        );
        setSearchResults(res.data ?? []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
  }, [search]);

  const fullName = (u: UserResult) =>
    [u.first_name, u.last_name].filter(Boolean).join(" ").trim() ||
    u.email ||
    u._id;

  const filteredLocations =
    locationSearch.trim().length < 1
      ? allLocations
      : allLocations.filter(
          (l) =>
            l.name.toLowerCase().includes(locationSearch.toLowerCase()) ||
            (l.city ?? "").toLowerCase().includes(locationSearch.toLowerCase())
        );

  const handleSendTomra = async () => {
    if (!selectedUser) return;
    let items: any[];
    try {
      items = JSON.parse(tomraItemsJson);
    } catch {
      setAlert({
        isOpen: true,
        message: "Items JSON is invalid.",
        severity: "error",
      });
      return;
    }
    const refund = Number(tomraRefund);
    if (!refund || refund <= 0) {
      setAlert({
        isOpen: true,
        message: "Refund must be a positive number.",
        severity: "error",
      });
      return;
    }
    setTomraLoading(true);
    setTomraResult(null);
    try {
      const res = await axios.post("simulate/tomra", {
        userId: selectedUser._id,
        refund,
        items,
        ...(selectedLocation
          ? {
              locationId: selectedLocation._id,
              locationName: selectedLocation.name,
            }
          : {}),
      });
      setTomraResult(res.data);
      setAlert({
        isOpen: true,
        message: "TOMRA drop simulated successfully!",
        severity: "success",
      });
    } catch (e: any) {
      setAlert({
        isOpen: true,
        message: e?.response?.data?.message ?? "Failed to simulate TOMRA drop.",
        severity: "error",
      });
    } finally {
      setTomraLoading(false);
    }
  };

  const handleBeevisionLoading = async () => {
    if (!selectedUser) return;
    setBvActionLoading(true);
    setBvResult(null);
    try {
      await axios.post("simulate/beevision/loading", {
        userId: selectedUser._id,
      });
      setBvLoadingState("loading_sent");
      setAlert({
        isOpen: true,
        message: "Loading state set. User is now in scanning state.",
        severity: "success",
      });
    } catch (e: any) {
      setAlert({
        isOpen: true,
        message: e?.response?.data?.message ?? "Failed to set loading state.",
        severity: "error",
      });
    } finally {
      setBvActionLoading(false);
    }
  };

  const handleBeevisionDrop = async () => {
    if (!selectedUser) return;
    setBvActionLoading(true);
    setBvResult(null);
    try {
      const res = await axios.post("simulate/beevision/drop", {
        userId: selectedUser._id,
        weight: Number(bvWeight),
        aiProcessorResult: bvAiResult,
        statusCode: Number(bvStatusCode),
        serialNumber: bvSerialNumber || undefined,
        ...(selectedLocation ? { locationId: selectedLocation._id } : {}),
      });
      setBvResult(res.data);
      setBvLoadingState("drop_sent");
      setAlert({
        isOpen: true,
        message: "Beevision drop simulated! Check the app.",
        severity: "success",
      });
    } catch (e: any) {
      setAlert({
        isOpen: true,
        message:
          e?.response?.data?.message ?? "Failed to simulate Beevision drop.",
        severity: "error",
      });
    } finally {
      setBvActionLoading(false);
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <div>
      <h1 className="font-satoshi text-2xl font-bold text-dark mb-6">
        Webhook Simulator
      </h1>

      <div className="flex flex-col sm:flex-row gap-4">
        {/* Left panel */}
        <div className="w-full sm:w-80 flex-shrink-0 space-y-4">
          {/* User search */}
          <div className="bg-white rounded-xl drop-shadow-none">
            <div className="w-full border-b border-gray-border px-7 py-4">
              <p className="font-satoshi font-medium text-base text-dark">
                Select User
              </p>
            </div>
            <div className="px-7 py-5 space-y-3">
              <input
                type="text"
                className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm font-satoshi focus:outline-none focus:border-droppie-oranje"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSelectedUser(null);
                }}
              />
              {searchLoading && (
                <p className="text-xs text-gray-400 font-satoshi">
                  Searching...
                </p>
              )}
              {searchResults.length > 0 && !selectedUser && (
                <ul className="border border-gray-border rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                  {searchResults.map((u) => (
                    <li
                      key={u._id}
                      className="px-4 py-2.5 hover:bg-orange-50 cursor-pointer"
                      onClick={() => {
                        setSelectedUser(u);
                        setSearch(fullName(u));
                        setSearchResults([]);
                      }}
                    >
                      <p className="text-sm font-satoshi font-medium text-dark leading-tight">
                        {fullName(u)}
                      </p>
                      <p className="text-xs font-satoshi text-gray-400">
                        {u.email}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {selectedUser && (
                <div className="flex items-center justify-between bg-orange-50 border border-droppie-oranje/20 rounded-lg px-3 py-2.5">
                  <div>
                    <p className="text-sm font-satoshi font-semibold text-dark leading-tight">
                      {fullName(selectedUser)}
                    </p>
                    <p className="text-xs font-satoshi text-gray-400">
                      {selectedUser.email}
                    </p>
                  </div>
                  <button
                    className="text-xs font-satoshi text-gray-400 hover:text-red-500 ml-3 flex-shrink-0"
                    onClick={() => {
                      setSelectedUser(null);
                      setSearch("");
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Location picker */}
          <div className="bg-white rounded-xl drop-shadow-none">
            <div className="w-full border-b border-gray-border px-7 py-4">
              <p className="font-satoshi font-medium text-base text-dark">
                Location{" "}
                <span className="text-gray-400 font-normal text-sm">
                  (optional)
                </span>
              </p>
            </div>
            <div className="px-7 py-5 space-y-3">
              {selectedLocation ? (
                <div className="flex items-start justify-between bg-orange-50 border border-droppie-oranje/20 rounded-lg px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-satoshi font-semibold text-dark leading-tight truncate">
                      {selectedLocation.name}
                    </p>
                    {selectedLocation.city && (
                      <p className="text-xs font-satoshi text-gray-400">
                        {selectedLocation.city}
                      </p>
                    )}
                  </div>
                  <button
                    className="text-xs font-satoshi text-gray-400 hover:text-red-500 ml-3 flex-shrink-0"
                    onClick={() => {
                      setSelectedLocation(null);
                      setLocationSearch("");
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div ref={locationRef}>
                  <input
                    type="text"
                    className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm font-satoshi focus:outline-none focus:border-droppie-oranje"
                    placeholder="Search location..."
                    value={locationSearch}
                    onFocus={() => setLocationDropdownOpen(true)}
                    onChange={(e) => {
                      setLocationSearch(e.target.value);
                      setLocationDropdownOpen(true);
                    }}
                  />
                  {locationDropdownOpen && filteredLocations.length > 0 && (
                    <ul className="mt-1 border border-gray-border rounded-lg divide-y divide-gray-100 max-h-52 overflow-y-auto bg-white">
                      {filteredLocations.slice(0, 20).map((l) => (
                        <li
                          key={l._id}
                          className="px-4 py-2.5 hover:bg-orange-50 cursor-pointer"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSelectedLocation(l);
                            setLocationSearch("");
                            setLocationDropdownOpen(false);
                          }}
                        >
                          <p className="text-sm font-satoshi font-medium text-dark leading-tight">
                            {l.name}
                          </p>
                          {l.city && (
                            <p className="text-xs font-satoshi text-gray-400">
                              {l.city}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {!selectedLocation && (
                <p className="text-xs font-satoshi text-gray-400">
                  If not selected, the machine's location is used.
                </p>
              )}
            </div>
          </div>

          {/* Machine type */}
          <div className="bg-white rounded-xl drop-shadow-none">
            <div className="w-full border-b border-gray-border px-7 py-4">
              <p className="font-satoshi font-medium text-base text-dark">
                Machine Type
              </p>
            </div>
            <div className="px-7 py-5 space-y-2">
              {(["tomra", "beevision"] as Tab[]).map((t) => (
                <div
                  key={t}
                  onClick={() => setTab(t)}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    tab === t
                      ? "border-droppie-oranje bg-orange-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${tab === t ? "border-droppie-oranje" : "border-gray-300"}`}
                  >
                    {tab === t && (
                      <div className="w-2 h-2 rounded-full bg-droppie-oranje" />
                    )}
                  </div>
                  <div>
                    <p className="font-satoshi font-medium text-sm text-dark">
                      {t === "tomra" ? "TOMRA" : "Beevision (Dropbot)"}
                    </p>
                    <p className="font-satoshi text-xs text-gray-400">
                      {t === "tomra"
                        ? "Reverse vending machine"
                        : "AI plastic scanner"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right panel */}
        <div className="flex-1 bg-white rounded-xl drop-shadow-none">
          {/* TOMRA */}
          {tab === "tomra" && (
            <>
              <div className="w-full border-b border-gray-border px-7 py-4">
                <p className="font-satoshi font-medium text-base text-dark">
                  TOMRA Drop
                </p>
                <p className="font-satoshi text-xs text-gray-text mt-0.5">
                  Simulates a{" "}
                  <code className="bg-gray-100 px-1 rounded text-xs">
                    tomra.consumersession.received
                  </code>{" "}
                  event - creates a drop and sends an FCM notification.
                </p>
              </div>
              <div className="px-7 py-6 space-y-5">
                <div>
                  <p className="font-satoshi font-medium text-sm text-dark mb-1.5">
                    Refund{" "}
                    <span className="text-gray-400 font-normal">
                      (cents - 150 = €1.50; ignored when items are given, the
                      total is taken from them)
                    </span>
                  </p>
                  <input
                    type="number"
                    min={1}
                    className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm font-satoshi focus:outline-none focus:border-droppie-oranje"
                    value={tomraRefund}
                    onChange={(e) => setTomraRefund(e.target.value)}
                  />
                </div>
                <div>
                  <p className="font-satoshi font-medium text-sm text-dark mb-1.5">
                    Items{" "}
                    <span className="text-gray-400 font-normal">
                      (JSON - refund is per item, in tenths of a cent, as TOMRA
                      sends it: 150 = €0.15)
                    </span>
                  </p>
                  <textarea
                    rows={10}
                    className="w-full border border-gray-border rounded-lg px-3 py-2 text-xs font-mono resize-y focus:outline-none focus:border-droppie-oranje"
                    value={tomraItemsJson}
                    onChange={(e) => setTomraItemsJson(e.target.value)}
                  />
                </div>
                {selectedLocation && (
                  <div className="flex items-center gap-2 text-xs font-satoshi text-gray-500 bg-gray-50 border border-gray-border rounded-lg px-3 py-2">
                    <span>
                      Drop location:{" "}
                      <span className="font-semibold text-dark">
                        {selectedLocation.name}
                      </span>
                    </span>
                  </div>
                )}
                <PrimaryButton
                  label={tomraLoading ? "Sending..." : "Send TOMRA Drop"}
                  disabled={!selectedUser || tomraLoading}
                  onClick={handleSendTomra}
                />
                {tomraResult && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="font-satoshi text-xs font-semibold text-green-700 mb-2">
                      Drop created successfully
                    </p>
                    <pre className="text-xs text-green-800 whitespace-pre-wrap font-mono">
                      {JSON.stringify(tomraResult, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Beevision */}
          {tab === "beevision" && (
            <>
              <div className="w-full border-b border-gray-border px-7 py-4">
                <p className="font-satoshi font-medium text-base text-dark">
                  Beevision Drop
                </p>
                <p className="font-satoshi text-xs text-gray-text mt-0.5">
                  Two-step flow - mirrors the real machine behaviour.
                </p>
              </div>
              <div className="px-7 py-6 space-y-5">
                {/* Warning */}
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                  <span className="text-amber-400 mt-0.5">⚠</span>
                  <p className="font-satoshi text-xs text-amber-800">
                    <span className="font-semibold">Before starting:</span> the
                    selected user must have the{" "}
                    <span className="font-semibold">QR sheet open</span> in the
                    app (home screen → scan button). This activates the polling
                    loop.
                  </p>
                </div>

                {/* Step 1 */}
                <div
                  className={`rounded-xl border-2 p-5 transition-all ${bvLoadingState !== "idle" ? "border-green-300 bg-green-50" : "border-gray-200"}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 font-satoshi ${bvLoadingState !== "idle" ? "bg-green-500 text-white" : "bg-gray-100 text-gray-500"}`}
                    >
                      {bvLoadingState !== "idle" ? "✓" : "1"}
                    </div>
                    <div>
                      <p className="font-satoshi font-semibold text-sm text-dark">
                        Start Loading
                      </p>
                      <p className="font-satoshi text-xs text-gray-400">
                        Sets{" "}
                        <code className="bg-gray-100 px-1 rounded">
                          beevisionStatus = true
                        </code>{" "}
                        - app shows scanning screen.
                      </p>
                    </div>
                  </div>
                  {bvLoadingState === "idle" ? (
                    <SecondaryButton
                      label={bvActionLoading ? "Setting..." : "Start Loading"}
                      disabled={!selectedUser || bvActionLoading}
                      onClick={handleBeevisionLoading}
                    />
                  ) : (
                    <p className="font-satoshi text-xs text-green-700 font-semibold">
                      ✓ Loading state sent
                    </p>
                  )}
                </div>

                {/* Step 2 */}
                <div
                  className={`rounded-xl border-2 p-5 space-y-4 transition-all ${bvLoadingState === "idle" ? "opacity-40 pointer-events-none border-gray-200" : bvLoadingState === "drop_sent" ? "border-green-300 bg-green-50" : "border-gray-200"}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 font-satoshi ${bvLoadingState === "drop_sent" ? "bg-green-500 text-white" : "bg-gray-100 text-gray-500"}`}
                    >
                      {bvLoadingState === "drop_sent" ? "✓" : "2"}
                    </div>
                    <div>
                      <p className="font-satoshi font-semibold text-sm text-dark">
                        Send Drop Result
                      </p>
                      <p className="font-satoshi text-xs text-gray-400">
                        Pre-registers the drop and sends FCM notification.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="font-satoshi font-medium text-xs text-dark mb-1.5">
                        Weight (kg)
                      </p>
                      <input
                        type="number"
                        step="0.01"
                        className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm font-satoshi focus:outline-none focus:border-droppie-oranje"
                        value={bvWeight}
                        onChange={(e) => setBvWeight(e.target.value)}
                      />
                    </div>
                    <div>
                      <p className="font-satoshi font-medium text-xs text-dark mb-1.5">
                        Status Code{" "}
                        <span className="text-gray-400 font-normal">
                          (≥1 = OK)
                        </span>
                      </p>
                      <input
                        type="number"
                        className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm font-satoshi focus:outline-none focus:border-droppie-oranje"
                        value={bvStatusCode}
                        onChange={(e) => setBvStatusCode(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <p className="font-satoshi font-medium text-xs text-dark mb-1.5">
                      Serial Number{" "}
                      <span className="text-gray-400 font-normal">
                        (optional)
                      </span>
                    </p>
                    <input
                      type="text"
                      className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm font-satoshi focus:outline-none focus:border-droppie-oranje"
                      value={bvSerialNumber}
                      onChange={(e) => setBvSerialNumber(e.target.value)}
                      placeholder="SIM-001"
                    />
                  </div>

                  <div>
                    <p className="font-satoshi font-medium text-xs text-dark mb-1.5">
                      AI Processor Result{" "}
                      <span className="text-gray-400 font-normal">
                        (include "plastic"+"milk" for material popup)
                      </span>
                    </p>
                    <textarea
                      rows={3}
                      className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm font-satoshi resize-y focus:outline-none focus:border-droppie-oranje"
                      value={bvAiResult}
                      onChange={(e) => setBvAiResult(e.target.value)}
                    />
                  </div>

                  {selectedLocation && (
                    <div className="flex items-center gap-2 text-xs font-satoshi text-gray-500 bg-gray-50 border border-gray-border rounded-lg px-3 py-2">
                      <span>📍</span>
                      <span>
                        Drop location:{" "}
                        <span className="font-semibold text-dark">
                          {selectedLocation.name}
                        </span>
                      </span>
                    </div>
                  )}

                  {bvLoadingState === "loading_sent" && (
                    <PrimaryButton
                      label={
                        bvActionLoading ? "Sending..." : "Send Drop Result"
                      }
                      disabled={bvActionLoading || !bvAiResult}
                      onClick={handleBeevisionDrop}
                    />
                  )}

                  {bvResult && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="font-satoshi text-xs font-semibold text-green-700 mb-2">
                        Drop pre-registered
                      </p>
                      <pre className="text-xs text-green-800 whitespace-pre-wrap font-mono">
                        {JSON.stringify(bvResult, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>

                {bvLoadingState !== "idle" && (
                  <button
                    className="font-satoshi text-xs text-gray-400 hover:text-gray-600 underline"
                    onClick={() => {
                      setBvLoadingState("idle");
                      setBvResult(null);
                    }}
                  >
                    Reset simulation
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(WebhookSimulatorScreen);
